import { ReactNode } from 'react';
import type { AiRecommendation } from './workload';

export const linkClass =
  'font-medium text-blue-700 underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600';
export const controlClass =
  'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600';

export function Card({ title, subtitle, action, children, className = '', labelledBy }: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  labelledBy: string;
}) {
  return (
    <section className={`min-w-0 rounded-xl border border-slate-200 bg-white ${className}`} aria-labelledby={labelledBy}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div className="min-w-0">
          <h2 id={labelledBy} className="text-base font-semibold text-slate-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function DataState({ loading, error, retry, rows = 3 }: { loading: boolean; error: string | null; retry: () => void; rows?: number }) {
  if (loading)
    return (
      <div className="space-y-3 px-5 py-5" role="status" aria-label="Cargando datos">
        {Array.from({ length: rows }, (_, i) => <div key={i} className="h-4 animate-pulse rounded bg-slate-100 motion-reduce:animate-none" style={{ width: `${90 - i * 15}%` }} />)}
      </div>
    );
  if (error)
    return (
      <div className="px-5 py-5 text-sm text-red-800" role="alert">
        {error}{' '}
        <button type="button" className={linkClass} onClick={retry}>Reintentar</button>
      </div>
    );
  return null;
}

export type Tone = 'neutral' | 'good' | 'watch' | 'bad';
const toneText: Record<Tone, string> = { neutral: 'text-slate-900', good: 'text-emerald-700', watch: 'text-amber-800', bad: 'text-red-700' };

/** One KPI inside a divided strip. Keep the value short; put context in `hint`. */
export function Stat({ label, value, hint, tone = 'neutral', children }: { label: string; value: ReactNode; hint?: ReactNode; tone?: Tone; children?: ReactNode }) {
  return (
    <div className="min-w-0 bg-white p-5">
      <p className="text-sm font-medium text-slate-600">{label}</p>
      <p className={`mt-2 text-3xl font-semibold tabular-nums tracking-tight ${toneText[tone]}`}>{value}</p>
      {hint && <p className="mt-1 text-xs leading-relaxed text-slate-500">{hint}</p>}
      {children}
    </div>
  );
}

export const ageTone = (days: number): Tone => (days <= 2 ? 'good' : days <= 7 ? 'watch' : 'bad');
const pill: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
  good: 'bg-green-50 text-green-800 ring-green-200',
  watch: 'bg-amber-50 text-amber-800 ring-amber-200',
  bad: 'bg-red-50 text-red-800 ring-red-200',
};

export function Pill({ tone = 'neutral', children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return <span title={title} className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${pill[tone]}`}>{children}</span>;
}

export const daysLabel = (days: number) => (days === 0 ? 'Hoy' : days === 1 ? '1 día' : `${days.toLocaleString('es-MX')} días`);

export function AgePill({ days }: { days: number }) {
  return <Pill tone={ageTone(days)} title="Tiempo desde la carga">{daysLabel(days)}</Pill>;
}

const AI_META: Record<AiRecommendation, { label: string; tone: Tone }> = {
  recommend_reject: { label: 'IA: rechazar', tone: 'bad' },
  recommend_approve: { label: 'IA: aprobar', tone: 'good' },
  uncertain: { label: 'IA: por resolver', tone: 'watch' },
};

export function AiPill({ recommendation }: { recommendation: AiRecommendation | null }) {
  if (!recommendation) return null;
  const meta = AI_META[recommendation];
  return <Pill tone={meta.tone}>{meta.label}</Pill>;
}

/** Horizontal proportion bar; segments carry their own accessible text in the legend. */
export function Meter({ value, max, tone = 'neutral' }: { value: number; max: number; tone?: Tone }) {
  const fill: Record<Tone, string> = { neutral: 'bg-slate-400', good: 'bg-emerald-600', watch: 'bg-amber-500', bad: 'bg-red-600' };
  const width = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="h-1.5 w-full rounded-full bg-slate-100" aria-hidden="true">
      <div className={`h-1.5 rounded-full ${fill[tone]}`} style={{ width: `${width}%` }} />
    </div>
  );
}
